<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

import SpaceForm from './SpaceForm.vue';

const props = defineProps({
  panelStyle: {
    type: Object,
    default: () => ({}),
  },
  active: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['close', 'saved', 'cancel']);

const store = useStore();
const { t } = useI18n();
const isSubmitting = ref(false);
const spaceFormRef = ref(null);

const handleClose = () => emit('close');
const handleCancel = () => {
  spaceFormRef.value?.resetForm();
  emit('cancel');
  emit('close');
};

const handleSubmit = async spaceDetails => {
  if (isSubmitting.value) return;

  isSubmitting.value = true;

  try {
    const response = await store.dispatch('spaces/create', spaceDetails);
    useAlert(t('SETTINGS.SPACES.MESSAGES.CREATE_SUCCESS'));
    spaceFormRef.value?.resetForm();
    emit('saved', response);
    handleClose();
  } catch (error) {
    let errorMessage = t('SETTINGS.SPACES.MESSAGES.CREATE_ERROR');

    if (error?.response?.data?.errors) {
      const errors = error.response.data.errors;
      const firstError = Object.values(errors)[0];

      if (firstError?.code) {
        const validationKey = `SETTINGS.SPACES.VALIDATION.${firstError.code}`;
        // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
        errorMessage = t(validationKey);
      } else if (firstError?.message) {
        errorMessage = firstError.message;
      }
    } else if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    }

    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({
  resetForm: () => spaceFormRef.value?.resetForm(),
});
</script>

<template>
  <div
    class="fixed w-[25rem] z-50 min-w-0 bg-n-alpha-3 backdrop-blur-[100px] p-6 rounded-xl border border-slate-50 dark:border-slate-900 shadow-md flex flex-col gap-6 max-h-[85vh] overflow-y-auto"
    :style="panelStyle"
    @click.stop
  >
    <h3 class="text-base font-medium text-slate-900 dark:text-slate-50">
      {{ t('SETTINGS.SPACES.CREATE') }}
    </h3>
    <SpaceForm
      ref="spaceFormRef"
      :active="props.active"
      :is-submitting="isSubmitting"
      @submit="handleSubmit"
      @close="handleClose"
      @cancel="handleCancel"
    />
  </div>
</template>
